/** @module csi/action/Controller */
define([
	"dojo/_base/array",
	"dojo/_base/connect",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/promise/all",
	"dojo/topic",
	"dojo/when",
	"src/c8-platform/core/request",
	"src/c8-platform/service/aggregateStoreService",
	"src/designSystem/types/button-types",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/DeferredTracking",
	"csi/_base/underscore",
	"csi/action",
	"csi/action/ActionView",
	"csi/action/FormData",
	"csi/comm",
	"csi/comm/PostRequest",
	"csi/def",
	"csi/def/Action",
	"csi/def/Context",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/resolver",
	"csi/service/openTelemetryService",
	"csi/service/writerHoldService",
	"csi/service/writerService",
	"csi/state",
	"csi/state/rootStore",
	"csi/state/viewUtils",
	"csi/view",
	"csi/view/ViewDialog",
	"csi/widget/MessageDialog",
	"csi/widget/Tooltip",
	"csi/xml",
	"csi/xml/QueryProcessor",
	"csi/xml/Writer"
], function(array, connect, declare, lang, dom, all, topic, when, {errorIsUnhandledException}, {clearAggregateStoreIdCache}, {ButtonEnum}, csi, Deferred, Tracking, _,
	csiAction, ActionView, FormData, comm, PostRequest, def, Action, Context, html, locale, message, resolver, openTelemetryService,
	{submitHoldWriter}, writerService, state, {routerStore, sessionStore}, {isReactView}, csiView, ViewDialog, MessageDialog, Tooltip, xml, QueryProcessor, Writer) {

	var _actionDialog;
	var _progressTopicHandle;

	function _setActionDialog(/*Dialog?*/dialog) {
		// summary: only ever allow one dialog at a time
		if (_actionDialog && _actionDialog.domNode) {
			if (_actionDialog.destroyOnHide) {
				_actionDialog.hide();
			} else {
				_actionDialog.destroy();
			}
		}

		if (_progressTopicHandle) {
			_progressTopicHandle.remove();
			_progressTopicHandle = null;
		}

		return _actionDialog = dialog;
	}

	function _mapConfirmIcon(/*String*/confirmButton) {
		var confirmIcon = MessageDialog.ICON_QUESTION;
		if (confirmButton) {
			switch (confirmButton) {
				case MessageDialog.BUTTON_DELETE:
				case MessageDialog.BUTTON_PURGE:
				case MessageDialog.BUTTON_PURGE_RESET:
				case MessageDialog.BUTTON_RESET:
				case MessageDialog.BUTTON_CHANGE:
				case MessageDialog.BUTTON_DISCARD:
					confirmIcon = MessageDialog.ICON_ERROR;
					break;
				case MessageDialog.BUTTON_REMOVE:
					confirmIcon = MessageDialog.ICON_WARNING;
			}
		}
		return confirmIcon;
	};

	/**
	 * static function declared in goToDialogService
	 * goToDialogService already importing this controller module, so to prevent circular dependency,
	 * in home.js we assign this function as static member to controller object
	 * also, to ensure goToDialogService is loaded, import of goToDialogService is added to csi/home.js
	 */
	//Controller.goToDialog - function

	var Controller = declare(null, {
		// _WidgetBase: widget controlling action (currently only set from some ToolbarPlugin actions)
		actionWidget: null,

		// Any: value from change to action widget (currently only set from some ToolbarPlugin actions)
		actionValue: null,

		/** @property {boolean} bPreventRepeat - if true, prevents repeat functionality on the action */
		bPreventRepeat: false,

		// Boolean: indicates if action was invoked by mouse click
		bClick: false,

		/** @property {boolean} bCacheResponse - if true, cache the nodes returned in the response */
		bCacheResponse: true,

		// Boolean: indicates if any data may have been changed by action
		bDataChanged: false,

		// Boolean: indicates that writer operations should not be published until user pauses
		bHold: false,

		/** @property {boolean} bInterrupt - if true, action can be interrupted by others */
		bInterrupt: false,

		// Boolean: indicates that action works alone and cannot be interrupted by another action
		bModal: true,

		// Boolean: indicates that action works on view refNode
		bRefNode: false,

		// Boolean: indicates that indeterminate progress dialog should be shown while performing action
		bShowProgress: false,

		// Boolean: indicates that wait screen should be shown while performing action
		bShowWait: false,

		/** @property {boolean} bWaitForDeferreds - if set to false, won't wait for Tracking.allDeferreds on start of action */
		bWaitForDeferreds: true,

		/** @property {boolean} closeDialogOnError - if set to true, then the react dialog will be closed when an error occurs rather than stay open (and show the error itself) */
		closeReactDialogOnError: false,

		// Object: class to use for confirmation dialog
		confirmDialogClass: MessageDialog,

		/** @property {Object} confirmValues - Hold the values from the confirm MessageBox */
		confirmValues: null,

		// final status of action
		endStatus: null,

		// tracking number for performance
		nTracking: null,

		/**
		 * extension point allowing additional logic when controller receives response
		 * async function that will be called in publishResponse function
		 *  @param {Controller} ac
		 */
		// onPublishResponseAsync: null,

		// Number: cummulative time for popup dialog
		popupDuration: 0,

		// String: topic to subscribe to for progress events
		progressTopic: "",

		// DOMNode: DOM element associated with node being acted upon
		refNode: null,

		/** @property {DOMNode} refNodeNext - refNode where new node(s) will be inserted before */
		refNodeNext: null,

		/** @property {DOMNode} refNodePrev - refNode where new node(s) will be inserted after */
		refNodePrev: null,

		/** @property {boolean} repeatReactForm - indicate if SaveNew button is clicked */
		repeatReactForm: false,

		/** @property {String} saveResourceId - id of a button to submit the action */
		saveResourceId: "Save",

		// DOMNode: DOM element which was original click target for event
		targetElement: null,

		/** @property {boolean} usedReactDialog - true if React dialog was used for action */
		usedReactDialog: false,

		/**@property {boolean} useReactForm - indicate if using the new React form */
		useReactForm: null,

		/** @property {boolean} waitForTrackingComplete - if true, then ensure that all other action controllers are complete before starting this one */
		waitForTrackingComplete: false,

		// csi.view.Widget: view widget on which controller is acting
		widget: null,

		// FormData: collects data for action from popup views
		//m_formData: null,

		// Controller: controller acting as proxy for this action in popup dialog, such as for Save button
		//m_proxy: null,

		// String: error message for action
		//m_error: null,

		// Response: response from publishing operations
		//m_response: null,

		// Boolean: indicates toggle value before action is applied
		m_bToggle: null,

		// Boolean: flag to indicate processing has been interrupted by another action
		//_bInterrupted: false,

		// Boolean: status of control keys during click to invoke action
		//_altKey: null,
		//_ctrlKey: null,
		//_shiftKey: null,

		/** @property {VoidFunction} _closeReactDialogHandle - function used to close React dialog when opened */
		_closeReactDialogHandle: null,

		// String: error code and exception from response
		//_errorCode: null,
		//_errorException: null,

		// Object[]: handles to event connections
		//_handles: null,

		// Object: handle to track suspended actions
		//_handleSuspend: null,

		// Object: handle to track modeless actions
		//_handleUnfocus: null,

		// Number: helps stop recursive behavior within _onPublishResponse
		_nPublished: 0,

		// Number: show dialog indicating progress if have not received response in given time
		//_nWaitForResponse: 0,

		// ViewDialog: popup dialog used for user input
		//_popupDialog: null,

		// Date: date object when action was suspended
		//_popupDate: null,

		// Object: timeout handle to track publish progress
		//_progressHandle: null,

		// Deferred: deferred object used to publish operations
		//_publishDfd: null,

		// String[]: list of urls modified by action (only stored here if a return request is issued, otherwise use response's list)
		//_urlsModified: null,

		/** @property {boolean} _useExternalWriter - indicates action controller was provided externally rather than created solely for this controller */
		_useExternalWriter: false,

		// Function: callback functions for controller
		//_fOnAction: null,
		//_fPostAction: null,
		//_fPostActionError: null,
		//_fValidateAction: null,

		// Span: OpenTelemetry span
		//_otelSpan: null,

		constructor: function(/*Object*/params) {
			// set action, container, element properties with any others passed in
			lang.mixin(this, params);

			if (!this.m_cache) {
				this.m_cache = this.widget ? this.widget.getCache() : resolver.m_cache;
			}

			// node/attribute context of action - parent is only valid if there is relationship attribute
			if (!this.m_urlNode) {
				this.m_urlNode = html.getElementContext(this.m_element);
			}

			if (!this.m_urlParent) {
				var domAction = html.getNodeWithDataAttribute(this.m_element, html.DATA_ACTION);
				this.m_idAttrParent = html.getDataAttribute(domAction, html.DATA_CONTEXT_PARENT_ATTR);
				this.m_urlParent = html.getDataAttribute(domAction, html.DATA_CONTEXT_PARENT_URL);
			}

			// get toggle state of action (if any)
			if (this.m_bToggle === null) {
				this.m_bToggle = this.m_action.isToggled(this.m_element);
			}
		},

		/**
		 * Annotate request with action id
		 * @param {Request} request
		 */
		annotateActionRequest(request) {
			request.addParameter("UI_Action", this.getRequestActionId());
		},

		isValidAction: function(/*Boolean*/bEnd) {
			const {widget} = this;

			if (!widget || widget.omitValidActionCheck || widget.isValidView()) {
				return true;
			}

			if (bEnd) {
				this.postProcess();
			}

			return false;
		},

		hasPendingAction: function() {
			return false;
		},

		getAction: function() {
			return this.m_action;
		},

		getCache: function() {
			return this.m_cache;
		},

		getConfirmButton: function() {
			return (this.m_bToggle ? this.m_action.confirmActionTG : null) || this.m_action.confirmAction || MessageDialog.BUTTON_YES;
		},

		getConfirmIcon: function() {
			return this.m_action.confirmIcon || _mapConfirmIcon(this.getConfirmButton());
		},

		getNodeURL: function() {
			return this.m_urlNode;
		},

		getNode: function() {
			return resolver.resolveNode(this.m_urlNode, false, this.m_cache);
		},

		getActionNodeType: function() {
			// summary: return the type of the node the controller is acting upon
			var node = this.getNode();
			return node ? node.$Type : null;
		},

		getContext: function() {
			// summary: return context for action
			// FIX: move _ctx from csi.heading.AttributeController to Controller
			var ctx = this._ctx;
			if (!ctx) {
				if (this.widget) {
					ctx = this.widget.getContext(this.m_urlNode, this.m_urlParent, this.m_idAttrParent);
				} else {
					ctx = new Context(null, this.m_cache, this.m_urlNode, this.m_urlParent, this.m_idAttrParent);
				}
			}

			ctx.ac = this;
			return ctx;
		},

		getParentURL: function() {
			// summary: if parent url was not known when action created, get it now on demand
			this.m_urlParent = this.m_urlParent || this.getContext().getParentURL();
			return this.m_urlParent;
		},

		getParent: function() {
			return resolver.resolveNode(this.getParentURL(), false, this.m_cache);
		},

		getParentAttributeType: function() {
			return xml.nodeAttributeGetType(this.getParent(), this.m_idAttrParent);
		},

		getParentAttributeValue: function() {
			var parent = this.getParent();
			return parent ? parent[this.m_idAttrParent] : null;
		},

		getChildIndex: function() {
			// summary: return index of child in parent attribute
			var value = this.getParentAttributeValue();
			return value ? array.indexOf(value, this.m_urlNode) : -1;
		},

		getIntroString: function() {
			// summary: return string used as introduction to popup dialog
			return this.getString(csiAction.SUFFIX_DETAIL);
		},

		/**
		 * Return id for action to be used in action requests
		 * @return {string}
		 */
		getRequestActionId() {
			const {m_action, heading} = this;
			return `${m_action.m_id}${heading ? ` ${heading.getKeyDP()}` : ""}`
		},

		getString: function(/*String*/suffix, /*String[]?*/params) {
			return this.m_action.getString(suffix, params || this.getStringParameters());
		},

		getStringParameters: function() {
			// summary: use return value as default parameter to success message
			var v = this.m_response && this.m_response.getReturnValue ? this.m_response.getReturnValue() : null;

			if (v != null && !_.isArray(v)) {
				return [v];
			}

			return v;
		},

		getTitle: function() {
			// summary: description for performance tracking
			var s = this.getString(csiAction.SUFFIX_TITLE) || this.m_action.getDescription(this.m_bToggle);
			return s ? s.replace(/\.\.\./, "") : null;
		},

		getTrackingId: function() {
			// summary: return id for action tracking
			const s = this.getRequestActionId() + ": " + routerStore.locationType;
			const v = this.widget?.defView.getKey();
			return s + (v ? (" - " + v) : "");
		},

		getMessage: function(/*String[]?*/params) {
			return this.getString(csiAction.SUFFIX_MESSAGE, params);
		},

		getSuccess: function(/*String[]?*/params) {
			var s;
			if (this.m_bToggle) {
				s = this.getString(csiAction.SUFFIX_TG_SUCCESS, params);
			}
			return s || this.getString(csiAction.SUFFIX_SUCCESS, params);
		},

		/**
		 * Return error to display to user
		 * @param {string} [suffix]
		 * @param {string[]} [params]
		 * @return {string}
		 */
		getError(suffix, params) {
			// if suffix provided, then it takes precedence
			if (suffix) {
				return this.getString(suffix, params);
			}

			// if error was encountered and stored, then use it
			if (this.m_error != null) {
				return this.m_error;
			}

			// use default error for action
			return this.getString(csiAction.SUFFIX_ERROR, params);
		},

		getModifiedUrls: function() {
			// summary: return list of urls modified by request
			return this._urlsModified || (this.m_response && this.m_response.getModifiedUrls());
		},

		getActionView: function(/*ViewRef?*/vr) {
			// summary: return event object containing reference to popup view
			if (!vr) return null;

			// first try to find view in context of node being acted on
			const {viewParams} = vr;
			var idView = vr.m_id;
			var typeView = vr.m_typeView;
			var url = this.m_urlNode ? this.m_urlNode : this.m_urlParent;
			var nodeContext = this.m_cache.get(url, true);
			var nodeReferenced = vr.getReferencedNode(this.m_cache, nodeContext);
			var node = nodeReferenced;

			var defView = def.getNodeViewDef(nodeReferenced, idView, false, false, typeView);
			if (!defView && (idView != def.DEFAULT_FORM_ID)) {
				// second try to find view in context of current container - don't do if default form which should only exist on target node type
				var nodeContainer = this.m_cache.get(this.widget.getViewURL());
				defView = def.getNodeViewDef(nodeContainer, idView, false, false, typeView);
				node = nodeContainer;
			}

			if (defView) {
				// assign any view parameters
				if (viewParams) {
					defView.classControllerParams = {
						...defView.classControllerParams,
						...viewParams,
					};
				}

				return new ActionView(this, defView, node);
			}

			if (idView != def.DEFAULT_FORM_ID) {
				console.warn("getActionView - could not resolve ViewRef with Id='" + idView + "'. nodeContext='" + xml.nodeGetURL(nodeContext) + "' nodeReferenced='" + xml.nodeGetURL(nodeReferenced) + "' nodeContainer='" + xml.nodeGetURL(nodeReferenced) + "'");
			}

			return null;
		},

		getDialog: function() {
			// summary: return enclosing dialog if in popup
			var w = this.widget;
			if (w.bPopup) {
				while (w = w.getParent()) {
					if (w instanceof ViewDialog) {
						return w;
					}
				}
			}

			return null;
		},

		getFormData: function() {
			if (!this.m_formData) {
				this.m_formData = new FormData(this);
				this.onFormData(this.m_formData);
			}

			return this.m_formData;
		},

		/**
		 * Return proxy action for popup dialog.
		 * @param {string} id
		 * @param {boolean} bSave - Indicates whether this is a save action.
		 * @param {Object} params - Additional parameters.
		 * @returns {Object} Proxy action.
		 */
		getProxyAction(id, bSave, params) {
			const bNavigate = id === ButtonEnum.BUTTON_SAVE_GO || id === ButtonEnum.BUTTON_FINISH_GO;
			const action = Object.assign(new Action(), {
				bNavigate,
				m_id: id,
				m_tag: "Proxy",
				m_typeControl: def.ACTION_BUTTON,
				m_ac: this
			});

			params = params || {};

			if (!params.m_class) {
				const css = [params.m_bSubmit ? html.CSS_BUTTON_PRIMARY : html.CSS_BUTTON_SECONDARY];

				if (bSave) {
					css.push(html.CSS_ENABLE_ON_CHANGE);
				}

				params.m_class = css.join(" ");
			}

			return Object.assign(action, params);
		},

		addProxyActions: function(/*View*/defView, /*Actions*/defActions) {
			// for the react component, not necessary to add Actions
			if (isReactView(defView)) {
				return;
			}

			// add actions for popup view - they will replace that of the current view
			if (!defActions.hasActions()) {
				defActions.addAction(this.getProxyAction(this.saveResourceId, true, {m_bSubmit: true}));
			}

			defActions.addAction(this.getProxyAction(ButtonEnum.BUTTON_CANCEL, false, {m_class: html.CSS_BUTTON_LINK}));
		},

		applyActionFilters: function(/*csi.view.Widget*/view) {
			// summary: process expression in filters
			var node = this.getNode() || this.getParent();
			var cache = this.m_cache;

			// FIX: should these be added or kept separately from Relationship so they
			// do not conflict with other actions that add their own filters?
			if (view.relRoot) {
				view.relRoot.addFilters(this._getFixedFilters(), cache, node);
			}

			// convert XML filter elements to objects - make sure to resolve references since these
			// filters are applied as table column filters whose reference values are already
			// converted to reference names
			if (view) {
				var q = _.clone(view.get("query"));

				_.each(this._getViewFilters(), function(f) {
					QueryProcessor.addFilterToQueryObject(q, f, node, cache);
				});

				view.set("query", q);
			}
		},

		canHoldChanges: function() {
			// summary: changes to writer cannot be held if they couldn't be reflected in the cache
			const {m_action, m_writer} = this;
			return m_writer.canCacheChanges() && !m_action.getRequestParameters();
		},

		hasMoreToProcess: function() {
			// summary: return true if controller should not end normally
			return false;
		},

		start() {
			const span = openTelemetryService.startSpan("c8.web.action");
			//TODO: move assignment of user-related attributes into some kind of utility function
			span.setAttribute("c8.user.cnl", sessionStore.userUrl);
			span.setAttribute("c8.user.name", sessionStore.userName);
			// Track ActionId and node as attributes
			span.setAttribute("c8.web.action.id", this.getRequestActionId());
			span.setAttribute("c8.web.action.node", xml.isValidURL(this.m_urlNode) ? this.m_urlNode : this.m_urlParent);
			this._otelSpan = span;

			openTelemetryService.executeInContext(span, () => this._start());
		},

		async _start() {
			// summary: start processing action
			const {bWaitForDeferreds, widget} = this;
			const action = this.m_action;
			let w = this.m_writer;
			let holdPromise;

			const createWriter = () => {
				// tag writer with parameters identifying action and view
				const writer = new Writer();
				this.annotateActionRequest(writer.m_request);
				widget?.annotateViewWriter(writer);
				return writer;
			};

			try {
				// do not hold changes if request has return parameters
				if (this.bHold && (action.bReleaseHold || action.hasReturnParameters() || action.getRequestParameters() || action.m_request)) {
					this.bHold = false;
				} else if (action.bReleaseHold) {
					console.warn(`Action (${action.m_id}): ReleaseHold not necessary on this action.`);
				}

				if (w) {
					// writer provided to controller - no need to create new one
					this._useExternalWriter = true;
				} else if (!this.bHold) {
					// make sure any held changes are published before processing non-hold action
					holdPromise = submitHoldWriter();

					// create new writer for controller - do not cache change operations if not holding on client
					w = createWriter();
					w.setCannotCache();
				} else {
					// if holding changes, then still use a new writer for this controller until ready to publish;
					// only then transfer the operations over to the shared writer. That way, if there are any JS errors
					// while executing this controller (such as in the watchAttribute function), the previously held operations
					// can still be published without any partial operations from this controller
					w = createWriter();
					w.cache = this.m_cache;
				}

				// start tracking action which will help keep track of all asynchronous behavior associated with action
				// and when it is finally complete
				var preActionPromise;

				if (this.isValidAction()) {
					this.m_writer = w;
					this._startTracking();

					// notify observers
					topic.publish("/csi/action/start", this);

					// set view refNode if action requires it and not already set
					if (this.bRefNode) {
						this._setRefNode(this.refNode || widget.getRefNode(this.m_element));
					}

					// call extension points
					this.onStart();

					// if not modal, then listen for changes to focus/visibility so action can be terminated
					if (!this.bModal) {
						state.set("modelessAction", true);
					}

					if (!this.bModal || this.bInterrupt) {
						this._handleUnfocus = topic.subscribe("/csi/action/unfocus", (domNode) => {
							if (!domNode || dom.isDescendant(this.m_element, domNode)) {
								this.interruptProcess();
							}
						});
					}

					// do any pre-processing necessary for action - if returns false then do not proceed
					preActionPromise = await this._preAction();
				}

				if (preActionPromise === false) {
					this.endProcess();
					return;
				}

				// wait for all other deferreds in addition to promise above
				const results = await all([preActionPromise, holdPromise, (bWaitForDeferreds && Tracking.allDeferreds())]);
				if (!this.isValidAction(true) || results[0] === false) {
					this.endProcess();
					return;
				}

				this._fOnAction = csi.resolveFunction(action.m_onAction, widget);
				this._fPostAction = csi.resolveFunction(action.m_postAction, widget);
				this._fPostActionError = csi.resolveFunction(action.m_postActionError, widget);
				this._fValidateAction = csi.resolveFunction(action.validateActionFunction, widget);

				if (this.hasToConfirmAction()) {
					await this.confirmAction({});
				} else {
					await this.preProcess();
				}
			} catch (err) {
				if (err) {
					this._setError(err);
					this.displayError();
				}

				this.endProcess();
			}
		},

		async preProcess() {
			// summary: start processing for action - popup referenced view if one, otherwise continue on to processing
			var av = this.getActionView(this.m_action.m_viewRef);
			if (this.isValidActionView(av?.m_defView)) {
				await this.openDialog(av);
			} else {
				this.process();
			}
		},

		/**
		 * If React component passes promise in onSave, then skip call to process
		 * @param {Promise<void>} [promise] - if promise is passed, then wait for promise to finish before continuing
		 */
		processReact(promise) {
			this.resumeProcess();

			if (promise) {
				this.bDataChanged = true;
				this._resetPublishStatus();
				this.waitForPublish(promise);
			} else {
				this.process();
			}
		},

		/**
		 * add/remove css class which will hide MUI dialogs
		 * used in "openDialog" to hide dialog content while it is loading
		 * @param {boolean} bHide - hide dialog if true
		 */
		hideMuiDialog: function(bHide) {
			document.body.classList.toggle("csi-hide-mui-dialog", bHide);
		},

		/**
		 * Make adjustments to view and widget parameters before opening dialog
		 * @param {View} defView
		 * @param {Record<string,any>} viewParams
		 */
		preOpenDialog(defView, viewParams) {
			const {widget} = this;

			// share cache unless either view requires its own, but make sure that timestamp is set
			// so that the view will query for latest data rather than reuse previous results
			if (widget && !widget.defView.bOwnCache && !defView.bOwnCache) {
				viewParams.cacheView = widget.cacheView;
				viewParams.refreshTimestamp = new Date().getTime();
			}

			this.onCreatePopupDefView(defView);
			this._onCreatePopupParams(viewParams);

			// view must be marked as popup
			viewParams.bPopup = true;
		},

		openDialog: async function(/*ActionView*/av) {
			// summary: create view to open in dialog
			var defView = av.m_defView;

			// pop up the React dialog
			if (isReactView(defView)) {
				const {componentName} = defView;
				const component = csi.syncRequire(componentName).default;
				const componentProps = await this._onReactViewProps();

				this.closeReactDialogOnError = true;
				this.useReactDialog(routerStore.openDialog(component, componentProps));
				return;
			}

			// open React dialog for table selection
			if ((defView.m_typeView === "Table") && defView.m_formData.m_bAllowSelections && Controller.goToDialog) {
				Controller.goToDialog(this, null, false, null, null, defView, av.m_node.$URL);
				return;
			}

			// get parameters for Dojo dialog
			var dialogParams = {closeTitle: locale.resource("Cancel")};
			var viewParams = {};

			this.preOpenDialog(defView, viewParams);
			this._onCreatePopupDialogParams(dialogParams);

			return csiView.newViewDeferred(defView, av.m_node, viewParams).then(view => {
				// summary: open view dialog to populate form data
				this.popupView = view;

				if (view && !this._bInterrupted) {
					this.setProgress(locale.resource("Action_Loading"));
					this._onCreatePopup(view);
					Tooltip.hide();

					const dialog = this._popupDialog = new ViewDialog(_.extend({
						autofocus: false,
						destroyOnHide: true,
						intro: this.getIntroString(),
						parseOnLoad: false,
						originalHeight: (view.defView.getType() == "Form") ? null : 350,
						showInvisibleOnStartup: true,
						title: this.getTitle(),
						onCancel: () => {
							this._onCancelPopup();
						}
					}, dialogParams));

					// add view to dialog and startup; at this point the dialog will be shown offscreen so that DOM
					// calculations can be done while view is loading. When done, resize and show view in proper position
					dialog.addChild(view);
					dialog.startup();

					when(view.startup(), () => {
						if (!this._bInterrupted) {
							dialog.forceResize();
							this.onLoadPopup(view);
							dialog.focus();
						}
					});
				}
			});
		},

		/**
		 * Close popup dialog
		 */
		closeDialog() {
			this.when(this._popupDialog?.hide(), () => {
				delete this._popupDialog;
			});
		},

		/**
		 * Close action dialog
		 */
		closeActionDialog() {
			_setActionDialog(null);
		},

		/**
		 * Close React dialog
		 */
		closeReactDialog() {
			const {_closeReactDialogHandle} = this;
			this._closeReactDialogHandle = null;
			_closeReactDialogHandle?.();
		},

		/**
		 * Suspend processing of the controller while a React dialog is open
		 * @param {VoidFunction} closeHandle - function to close the dialog
		 */
		useReactDialog(closeHandle) {
			this._closeReactDialogHandle = closeHandle;
			this.usedReactDialog = true;
			this.suspendProcess();
			Tooltip.hide();
		},

		/**
		 * Hide popup dialog
		 */
		hidePopup() {
			html.setVisible(this._popupDialog, false);
		},

		/**
		 * Show popup dialog
		 */
		showPopup() {
			html.setVisible(this._popupDialog, true);
		},

		/**
		 * Resume controller when React dialog is cancelled
		 */
		onCancelReactDialog() {
			if (this.isSuspended()) {
				this.resumeProcess();
				this.postProcess();
			}
		},

		/**
		 * Extension point to allow changes to view definition to popup
		 * @param {View} defView
		 */
		onCreatePopupDefView: function(defView) { },

		/**
		 * Extension point to allow changes to parameters used to initialize popup dialog
		 * @param {Object} dialogParams
		 */
		onCreatePopupDialogParams: function(dialogParams) { },

		/**
		 * Extension point for base internal/abstract classes to append parameters to the new view
		 * callers must call the inherited method for parameters to process completely and "wraps" the more public
		 * call of onCreatePopupDialogParams()
		 * @param {Object} dialogParams
		 */
		_onCreatePopupDialogParams: function(dialogParams) {
			this.onCreatePopupDialogParams(dialogParams);
		},

		/**
		 * Extension point for base internal/abstract classes to append parameters to the new view
		 * callers must call the inherited method for parameters to process completely and "wraps" the more public
		 * call of onCreatePopupParams()
		 * @param {Object} params
		 */
		_onCreatePopupParams: function(params) {
			this.onCreatePopupParams(params);
		},

		/**
		 * Extension point to allow changes to parameters used to initialize popup view
		 * @param {Object} params
		 */
		onCreatePopupParams: function(params) { },

		/**
		 * Resume controller when popup dialog is cancelled
		 */
		_onCancelPopup: function() {
			if (this.isSuspended()) {
				csiAction.stopNonModalAction();
				this.resumeProcess();
				this.postProcess();
			}
		},

		/**
		 * Base implementation to affect view before calling startup (can be overridden)
		 * @param {csi.view.Widget} view
		 */
		_onCreatePopup: function(view) {
			if (view.bSupportsQuerySettings) {
				this.applyActionFilters(view);
			}

			this.onCreatePopup(view);
		},

		/**
		 * Extension point for filling in properties to pass to React component
		 * @param {ModalDialogProps} props
		 */
		async onReactViewProps(props) {
			// NOTE: it is not encouraged to pass actionController to React component - instead extract
			// information from controller that is necessary for the dialog (such as creating the MobX store used in the view);
			// it is only added here for legacy use
			props.actionController = this;
		},

		/**
		 * Return properties to pass to React component
		 * @return {Promise<ModalDialogProps>}
		 */
		async _onReactViewProps() {
			const props = {
				onClose: () => this.onCancelReactDialog(),
				onSave: (promise) => this.processReact(promise),
				onSaveNewWithSaveForm: () => {
					this.repeatReactForm = true;
					this.saveReactDialog(true);
				},
				onSaveWithSaveForm: () => {
					this.repeatReactForm = false;
					this.saveReactDialog(false);
				},
				title: this.getTitle(),
			};

			await this.onReactViewProps(props);
			return props;
		},

		/**
		 * save the react dialog
		 */
		async saveReactDialog(saveNew) {
			const saveFormResultPromise = this.saveForm();
			const saveFormResult = await saveFormResultPromise;
			if (saveFormResult !== undefined) {
				if (saveNew) {
					this.store?.resetStore();
				}
				this.processReact(saveFormResultPromise);
			} else {
				this.repeatReactForm = true;
			}
		},

		/**
		 * custom save form implemented here
		 */
		async saveForm() {
		},

		/**
		 * Return properties to pass to Save dialog for use with action
		 * @param {ViewWidget} [widget]
		 * @return {Partial<SaveDialogProps>}
		 */
		getReactSaveDialogProps(widget) {
			// do everything in validate stage so that error messages in publish will appear in the main dialog rather than open up a separate error dialog
			return {
				onCancel: () => this.onCancelReactDialog(),
				onValidate: async () => {
					await this.validateAndProcess(widget);
					return true;
				},
				title: this.getTitle(),
			};
		},

		/**
		 * Extension point to affect view before calling startup
		 * @param {csi.view.Widget} view
		 */
		onCreatePopup: function(view) { },

		/**
		 * Extension point to add dependency paths necessary to process Controller
		 * @param {string[]} paths
		 */
		onDependencyPaths: function(paths) { },

		/**
		 * Extension point to do any cleanup work at end of action
		 */
		onEnd: function() { },

		/**
		 * Extension point to initialize form data for controller
		 * @param {FormData} fd
		 */
		onFormData: function(fd) { },

		onLoadPopup: function(/*csi.view.Widget*/view) {
			// summary: finish layout of dialog and end action tracking once dialog is popped up;
			// tracking will begin again once proxy action is clicked
			this.suspendProcess();
			this.setProgress(null);
			this._popupDialog?.show?.();
		},

		/**
		 * Extension point allowing additional deferreds to execute before executing action
		 * @param {Deferred[]} dfds
		 */
		onPreActionDeferreds: function(dfds) { },

		/**
		 * Extension point allowing additional logic when controller receives response
		 * @param {Error} [err]
		 */
		onPublishError: function(err) { },

		onPublishResponse: function(/*Response*/response) {
			// summary: extension point allowing additional logic when controller receives response
		},

		onSecondaryPublish: function(/*Writer*/w) {
			// summary: extension point allowing writing additional operations that can only be done after response to initial operations
		},

		onStart: function() {
			// summary: extension point to do any initialization work at start of action
		},

		/**
		 * Extension point called when controller starts publish operation
		 */
		onStartPublish() { },

		async confirmAction(/*Object*/params) {
			// summary: prompt user for response
			params = params || {};

			if (params.materialIcon == null) {
				params.materialIcon = this.getConfirmIcon();
			}

			if (!params.buttons) {
				params.buttons = [this.getConfirmButton() + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL];
			}

			if (!params.title) {
				params.title = (this.m_bToggle ? this.getString(csiAction.SUFFIX_PROMPT + "_TG") : null) ||
					this.getString(csiAction.SUFFIX_PROMPT) ||
					this.getString(csiAction.SUFFIX_TITLE) ||
					this.m_action.getDescription(this.m_bToggle);
			}

			if (params.message == null) {
				params.message = (this.m_bToggle ? this.getString(csiAction.SUFFIX_CONFIRM + "_TG") : null) ||
					this.getString(csiAction.SUFFIX_CONFIRM);
			}

			if (!params.onResponse) {
				params.onResponse = lang.hitch(this, this.onConfirm);
			}

			message.hide();

			if (params.message || params.onMessage) {
				_setActionDialog(new this.confirmDialogClass(params)).show();
			} else {
				await this.preProcess();
			}
		},

		displayErrorDialog: function(/*Object?*/params) {
			// summary: show error dialog rather than using status indicator based on given parameters
			// 	{
			//		// String[]: any number of strings each of while will become button - button clicked will be passed to onResponse function
			//		buttons: null,
			//
			//		// String: server exception to display along with error
			//		exception: null,
			//
			//		// String: standard icons include "iconError", "iconWarning", "iconQuestion", and "iconSuccess"
			//		iconClass: null,
			//
			//		// String: error message to display
			//		message: null,
			//
			//		// String: suffix to use to localize action error
			//		suffix: null,
			//
			//		// function(button): function used to respond to button clicks; if false, then no response
			//		onResponse: null,
			//
			//		// String: title for dialog
			//		title: null
			//	}

			// Hide "Wait" layer before showing the dialog because otherwise dialog will appear underneath of that layer
			if (this.bShowWait) {
				html.showWait(false);
			}

			params = params || {};
			params.message = params.message || this.getError(params.suffix);

			if (params.onResponse == null) {
				params.onResponse = lang.hitch(this, this.postProcess, null, null);
			}

			this.setProgress(null);
			this._resetProgressHandle();

			_setActionDialog(message.errorDialog(params));
		},

		displayProgressDialog: function(/*Object?*/params) {
			// summary: show progress dialog with given parameters
			params = params || {};
			params.message = params.message || this.getString(csiAction.SUFFIX_PROGRESS);
			params.title = params.title || this.getString(csiAction.SUFFIX_PROMPT) || this.m_action.getDescription(this.m_bToggle);

			if (params.progressValue == null) {
				params.progressValue = Infinity;
			}

			// don't fade-in progress dialog as the progress may be over before it even displays
			params.duration = 0;

			_setActionDialog(new MessageDialog(params)).show();

			// subscribe to topic yielding progress value if specified
			if (this.progressTopic) {
				_progressTopicHandle = topic.subscribe(this.progressTopic, lang.hitch(this, this.onProgressTopic));
			}
		},

		displayProgressValue: function(/*Number*/value) {
			// summary: update progress bar value as percentage from 0 to 1
			if (_actionDialog && _actionDialog.progressBar) {
				_actionDialog.progressBar.set("value", value);
			}
		},

		/**
		 * If configured to confirm action, then start now that confirmed
		 * @param {String} key - the button key hit by user
		 * @param {Object} values - hold the values from the MessageBox
		 * @param {Event} e
		 */
		async onConfirm(key, values, e) {
			if (key == this.getConfirmButton()) {
				this.confirmValues = values;
				await this.preProcess();
			} else {
				this.postProcess();
			}
		},

		onProgressTopic: function(/*Number*/value) {
			// summary: update progress dialog with new progress value
			this.displayProgressValue(value);
		},

		/**
		 * Perform custom validation for action controller; throw error if invalid
		 * @return {Promise<void>}
		 */
		async onValidate() { },

		/**
		 * Validate action to see if it can continue processing - pass in action controller if coming
		 * from a proxy, but we want to localize the error message based on the original controller
		 * @param {Controller} [ac]
		 * @return {Promise<void>}
		 */
		async validate(ac) {
			ac = ac || this;
			const {widget} = ac;

			// reset error before validating
			ac.m_error = null;

			return when()
				.then(() => this._validateForm(widget))
				.then(() => this.onValidate())
				.then(() => this._validateAction(widget));
		},

		/**
		 * Check for validation errors from a React dialog
		 * NOTE: this does not use validate function above as the React dialog may not
		 * have a Dojo view, so the action controller form data is all that needs to be
		 * validated
		 * @param {ViewWidget} view
		 * @return {Promise<void>}
		 */
		async validateAndProcess(widget) {
			// validate
			const errorSuffix = this.m_formData?.getValidationError();
			if (errorSuffix) {
				throw new Error(this.getError(errorSuffix));
			}

			if (widget) {
				await this._validateForm(widget);
			}

			await this.onValidate();
			await this._validateAction();

			// process
			this.resumeProcess();
			this.process();

			const {_publishDfd} = this;

			return new Promise((resolve, reject) => {
				when(_publishDfd, resolve, reject);
			});
		},

		process: function() {
			// if writer is shared, then reset cache to make sure operations apply to current cache
			if (this.m_writer.bShared) {
				this.m_writer.cache = this.m_cache;
			}

			// derived controllers can override this function
			var call = this.toWriter();

			// perform additional client processing for action (not handled by server request)
			this._onAction(call);

			// publish changes
			this.publish();
		},

		toWriter: function() {
			// summary: write request if given - otherwise derived controller should override to write operations
			const request = this._getActionRequest();
			return request ? this.toWriterRequest(this.getFormData()) : null;
		},

		toWriterFields: function(/*String*/urlNode, /*FormData?*/fd, /*Function*/filter) {
			// summary: publish field values if field is updated and has not been used as part of request or operation;
			// but if form has no context (node is null), then assume all fields are updated
			fd = fd || this.m_formData;

			if (fd) {
				writerService.writeFormFields(this.m_writer, urlNode, fd.getFields(), !!fd.getNode(), filter);
			}
		},

		toWriterRequest: function(/*FormData?*/fd) {
			// summary: write request using field values stored with form data
			var valueFunc;

			if (fd = fd || this.m_formData) {
				// create map of all fields using fixed parameter ids
				var params = {};

				array.forEach(fd.getFields(), function(f) {
					if (f.idParameter) {
						params[f.idParameter] = f;
					}
				});

				valueFunc = function(id) {
					return fd.getFieldValue(params[id] || fd.getField(id), true);
				};
			}

			var call = this._writeRequest(null, valueFunc);

			if (fd) {
				// some fields may be marked so that they are sent as parameters even though
				// not specified in request (for now only use case is for Style Copy)
				array.forEach(fd.getFields(), function(f) {
					if (f.idParameter && !f.m_bUsed) {
						call.addParameter(f.idParameter, f.getType(), fd.getFieldValue(f.getControlName()));
						f.m_bUsed = true;
					}
				});

				// add selected nodes - separated into own function so derived controllers can process them differently;
				// pass null for third parameter so that it can be overridden by derived controllers
				this.toWriterSelections(fd, call, null);
			}

			this._writeRequestParameters(call);

			return call;
		},

		toWriterSelections: function(/*FormData*/fd, /*CallMethod?*/call, /*String?*/id) {
			// summary: add parameter for selected URLs if any
			if (fd) {
				if (fd.m_bSelectOne) {
					var node = fd.getSelectedNode(true);
					if (node) {
						if (call) {
							call.addParameter(id || xml.PROP_AGGREGATE_ADD, xml.ATTR_REF, node.$URL);
						} else {
							this.m_writer.changeNode(this.m_urlNode).changeAttribute(id, xml.ATTR_REF, node.$URL);
						}
					}
				} else {
					var nodes = fd.getSelectedNodes(true);
					if (nodes.length) {
						var urls = array.map(nodes, xml.nodeGetURL);

						if (call) {
							call.addParameter(id || xml.PROP_AGGREGATE_ADDS, xml.ATTR_REFLIST, urls);
						} else {
							this.m_writer.changeParent(this.m_urlNode, id, xml.ATTR_REFLIST, urls, true);
						}
					} else if (fd.bPreselectAll) {
						call.addParameter(xml.PROP_AGGREGATE_ALL, xml.ATTR_BOOLEAN, true);
					}
				}
			}
		},

		publish: function() {
			// summary: publish pending data changes to server if so configured
			const {
				m_action,
				bHold,
				widget,
				m_writer,
				_useExternalWriter,
			} = this;

			const withholdChanges = _useExternalWriter && !m_writer.bReport;

			// initialize members tracking publish behavior
			this._resetPublishStatus();

			if (m_writer.isChanged() && !withholdChanges) {
				// mark controller if there has been a change to the data - this is just meant as
				// a guide for listeners of actions to know if data may have changed or not due to an action
				this.bDataChanged = true;

				if (bHold && this.canHoldChanges()) {
					// most any change can cause the aggregate store id to change, so need to error on side of caution and
					// clear cache even if changes are held; the next time a request goes out to get the aggregate store ids,
					// the held changes will be sent to the server and hopefully picked up when calculating new ids
					clearAggregateStoreIdCache();

					if (!_useExternalWriter) {
						widget.writeOperations((w) => {
							w.addOperationsFrom(m_writer);
						}, true);
					}

					this.postProcess(true);
				} else {
					// if configured to be held but there were changes that could not be cached, then
					// publish changes now and wait for response
					this.bHold = false;

					// apply any custom parameters to writer
					m_writer.mixinParameters(m_action.getRequestParameters());

					// publish operations and wait for response
					this.waitForPublish(writerService.submitWriter(m_writer));
				}
			} else {
				this.postProcess(true);
			}
		},

		setProgress: function(/*String?*/s) {
			// summary: use function to display progress message
			if (!s && (s !== null)) {
				s = this.getMessage();
			}

			Tracking.message(this.nTracking, s);
		},

		/**
		 * Wait for publish request to server to finish
		 * @param {Deferred} dfd
		 * @param {boolean} bProgressDialog
		 */
		waitForPublish: function(dfd, bProgressDialog) {
			if (this.bShowWait) {
				html.showWait(true);
			} else if (bProgressDialog || this.bShowProgress) {
				this.displayProgressDialog({
					closable: false,
					message: locale.resource("Action_ProgressWait")
				});
			}

			this.setProgress();
			this.onStartPublish();
			this._publishDfd = new Deferred();
			this.when(dfd, lang.hitch(this, this._onPublishResponse));

			// wait for desired time after publishing - if still not complete, then popup progress box to allow user to continue
			if (this._nWaitForResponse && !this._nPublished) {
				this._progressHandle = setTimeout(lang.hitch(this, this._onPublishProgress, {}), this._nWaitForResponse);
			}
		},

		/*
		 * Return this action's server operation
		 */
		_getActionRequest: function() {
			const { m_action, useReactForm } = this;

			if (useReactForm) {
				console.assert(!m_action.m_request, "Addition logic should go to the Customer Action bundle file");
			}

			return m_action.m_request;
		},

		_onPublishProgress: function(/*Object*/params) {
			// summary: popup progress dialog if action still processing
			if (this.isValidAction(true) && !this._nPublished) {
				this.displayProgressDialog(params);
			}
		},

		_onPublishError: function(/*Error*/err) {
			this.publishError(err);
		},

		publishError: function(/*Error*/err) {
			// summary: respond to publish error
			this.closeActionDialog();

			if (this.bShowWait) {
				html.showWait(false);
			}

			if (!this.isValidAction(true)) {
				return;
			}

			if (err) {
				this._setError(err);
			}

			this._nPublished = 1;
			this._publishDfd?.reject(err);
			this._publishDfd = null;
			this.onPublishError(err);
			this.postProcess(false);
		},

		_hasReturnNodeRequest: function() {
			return this.m_action.hasReturnParameters();
		},

		_onPublishResponse: function(/*Response*/response) {
			this.publishResponse(response);
		},

		async publishResponse(/*Response*/response) {
			// summary: if view no longer valid, then do nothing
			var cache = this.m_cache;
			this.closeActionDialog();

			if (!this.isValidAction(true)) {
				return;
			}

			this.m_response = response;

			if (this.bCacheResponse) {
				cache.cacheResponse(response);

				// cache the node into the related context if needed
				if (this.m_proxy) {
					this.m_proxy.m_cache.cacheResponse(response);
				}
			}

			this.onPublishResponse(response);

			// post request to get desired return nodes if they weren't asked for in publish
			if (this._hasReturnNodeRequest() && (this._nPublished < 1)) {
				this._nPublished = 1;

				var req = this.getReturnNodeRequest([]);
				if (req) {
					// if nodes are already in cache then don't need to make request, but pass "1" for time so that
					// nodes whose timestamps have been reset will be gotten
					req = cache.reduceNodesRequest(req, 1);
					if (req) {
						this.annotateActionRequest(req);
						this.widget.annotateViewRequest(req);
						this.when(req.post(), lang.hitch(this, this._onPublishResponse));
						return;
					}
				}
			}

			// allow additional operations based on initial changes before continuing to postProcess;
			// be careful not to publish the same operations again in cases where writer continues to hold all changes (in report mode)
			var w = this.m_writer;

			if (w && !w.isChanged() && (this._nPublished < 2)) {
				if (w.bShared) {
					w.cache = cache;
				}

				this._nPublished = 2;
				this.onSecondaryPublish(w);

				if (w.isChanged()) {
					w.addParameter("ReturnNodes", "All");
					this.when(writerService.submitWriter(w), lang.hitch(this, this._onPublishResponse));
					return;
				}
			}

			await this.onPublishResponseAsync?.(this);

			if (this.bShowWait) {
				html.showWait(false);
			}

			this._publishDfd?.resolve();
			this._publishDfd = null;
			this.postProcess(true);
		},

		getReturnNodeRequest: function(/*String[]*/urlsNew) {
			// summary: by default, examine modified nodes from response and query for all nodes that currently are in cache;
			// this technique will not work for actions that create new nodes (New, Copy, etc.)
			var cache = this.m_cache;
			var urls = this.m_response.getModifiedUrls() || [];
			var params = lang.clone(this.m_action.getReturnParameters());
			var key = comm.QUERY_URL;
			var value = params[key];

			// only worried about nodes that were previously in the cache
			urls = array.filter(urls, function(url) {return cache.get(url);});

			// add any new urls to the request
			if (urlsNew) {
				urls = urls.concat(urlsNew);
			}

			// add value from return parameters
			if (value) {
				urls = urls.concat(value);
			}

			if (!urls.length) {
				return null;
			}

			urls = params[key] = _.uniq(urls);
			this._urlsModified = _.clone(urls);

			// reset timestamps in cache so reduceNodesRequest does not ignore these URLs
			for (const url of urls) {
				cache.resetNodeTime(url);
			}

			return new PostRequest(comm.MOD_SEARCH, comm.OP_URL, params);
		},

		interruptProcess: function() {
			// summary: if another action is starting, then force finish of this action
			this._bInterrupted = true;

			if (this._handleSuspend) {
				this.resumeProcess();
			}

			this.closeDialog();
			this.postProcess();
		},

		isSuspended: function() {
			// summary: return true if action is currently suspended
			return !!this._handleSuspend;
		},

		/**
		 * This checks a gives Action View to see if it exists and then checks it's id and field length to ensure it is populated
		 */
		isValidActionView: function(defView) {
			return defView && (defView.m_id !== def.DEFAULT_FORM_ID || !!defView.m_fields?.length);
		},

		/**
		 * Post-processing after action has finished publishing changes to data
		 * @param {boolean} [bStatus]
		 * @param {boolean} [bRepeat]
		 */
		postProcess(bStatus, bRepeat) {
			const {
				bPreventRepeat,
				closeReactDialogOnError,
				m_proxy,
				repeatReactForm,
				_closeReactDialogHandle,
				_popupDialog,
				_publishDfd
			} = this;

			// Controller property "bPreventRepeat" logically contradict bRepeat==true
			console.assert(!(bPreventRepeat && bRepeat));

			bRepeat = bRepeat ||
				(m_proxy && ((bStatus && m_proxy.m_action.m_bRepeat) || (bStatus === false))) ||
				(_closeReactDialogHandle && (bStatus === false));

			// cancel deferred if it exists, which means the action was interrupted
			_publishDfd?.cancel();
			this._publishDfd = null;

			// close dialogs - it is possible there was an action dialog created even though there was nothing to do (Canvas Save)
			this.closeActionDialog();

			if (!repeatReactForm && (closeReactDialogOnError || (bStatus !== false))) {
				this.closeReactDialog();
			}

			// reset modeless status when non-modal action ends
			this._resetModeless();

			if (bStatus === true) {
				// stop listening to events so post-processing will not trigger dialog layout
				if (!bRepeat) {
					_popupDialog?.stopEvents?.();
				}

				// configurable post-processing on successful action
				this.postProcessSuccess();
			} else if (bStatus === false) {
				// explicitly check for false in case this function is called after ignoring action
				// when there should be no error message
				this.setProgress(null);
				this.displayError();

				if (!m_proxy && !_closeReactDialogHandle && !this.postProcessError()) {
					// enable actions in view now that action is finished - if proxy then this will be handled in postProcessProxy
					this.disableActionView(false);
				}
			} else {
				// hide any progress message
				this.setProgress(null);
				this.postProcessCancel();
			}

			// signal end to complete action
			this.endStatus = bStatus;

			if (bRepeat || this.hasMoreToProcess() || repeatReactForm) {
				// show success message
				if (bStatus) {
					message.success(this.getSuccess());
				}

				// if repeating action, then suspend controller and reset members
				this.suspendProcess();
				this._resetPublishStatus();
			} else {
				// otherwise end controller
				this.endProcess();
			}

			// complete proxy processing
			if (m_proxy) {
				this.postProcessProxy(bStatus);
			}
		},

		/**
		 * Enable/disable other actions in view, as well as any parent view(s)
		 * @param {boolean} disabled
		 */
		disableActionView(disabled) {
			const {widget} = this;

			if (widget?.isValidView()) {
				widget.viewParent?.set("disabled", disabled);
				widget.set("disabled", disabled);
			}
		},

		/**
		 * Determine if error was an unhandled server exception
		 * @return {boolean}
		 */
		_isUnhandledException() {
			return errorIsUnhandledException(this._errorCode);
		},

		displayError: function(/*String?*/error) {
			// summary: show error, but allow derived controllers to override how it is shown;
			// this function is called from postProcess, so no response function needed when displaying error dialog
			const {bHold, popupView, widget, _closeReactDialogHandle, _errorCode, _errorException} = this;
			const view = popupView || widget;

			error = error || this.getError();
			message.hide();

			// if react dialog is in use, then error will be shown there
			if (_closeReactDialogHandle) {
				return;
			}

			if (!this._isUnhandledException() &&
				view?.isValidView() &&
				view.isIsolatedView() &&
				view.errorForm(error, _errorCode)) {
				return;
			}

			if (bHold) {
				message.error(error);
			} else if (error) {
				// if used submitHoldWriter then the error dialog will be opened from that function
				this.displayErrorDialog({
					exception: _errorException,
					message: error,
					onResponse: false
				});
			}
		},

		/**
		 * Extension point for when controller ends without failure or success (i.e. cancel)
		 */
		postProcessCancel: function() { },

		postProcessError: function() {
			// summary: handle error in action with configurable post-processing - returns Deferred if any for action
			if (this._fPostActionError) {
				this.setProgress();
				return this._fPostActionError(this);
			}

			return null;
		},

		postProcessSuccess: function() {
			// summary: handle successful action with configurable post-processing - returns Deferred if any for action
			if (this._fPostAction) {
				this.setProgress();
				return this._fPostAction(this);
			}

			return null;
		},

		postProcessProxy: function(/*Boolean*/bStatus) {
			// summary: separate function to allow for derived classes to post-process proxy individually
			const {m_proxy} = this;
			this.m_proxy = null;
			this.setProgress(null);
			m_proxy?.postProcess?.(bStatus);
		},

		endProcess: function() {
			// summary: end action tracking
			if (this.bShowWait) {
				html.showWait(false);
			}

			// track time last action ended - this is repeated in "_end" as another action could be invoked before this one fully finishes;
			// in that case, we still want to know the time at which the last action ended when another is invoked
			state.set("lastActionEndTime", new Date().getTime());

			Tracking.end(this.nTracking);
		},

		resumeProcess: function() {
			// summary: resume a suspended controller
			if (this._popupDate) {
				this.popupDuration += (new Date().getTime()) - this._popupDate.getTime();
				delete this._popupDate;
			}

			if (this._handleSuspend) {
				this._handleSuspend.remove();
				delete this._handleSuspend;
			}

			if (this.nTracking == null) {
				this._startTracking();
				// console.log("resumeProcess: ", this.getTrackingId());
			}
		},

		suspendProcess: function() {
			// summary: temporarily suspend tracking of this action - call resumeProcess to resume
			this._handleSuspend = this._handleSuspend || topic.subscribe("/csi/action/stop", lang.hitch(this, this.interruptProcess));
			this._popupDate = this._popupDate || new Date();
			this._resetModeless();

			if (this.nTracking !== null) {
				// console.log("suspendProcess: ", this.getTrackingId());
				var n = this.nTracking;
				this.nTracking = null;
				Tracking.end(n);
			}
		},

		validateNode: function(url, bWithAttributes) {
			const {popupView, m_writer} = this;

			m_writer.validateNode(url, bWithAttributes);

			popupView?.combinedPathMap?.forEach((ep) => {
				m_writer.validateNode(xml.getUrlPath(url, ep.getStandardPath(), true), bWithAttributes);
			});
		},

		when: function(/*Object?*/valueOrPromise, /*Function?*/callback, /*Function?*/errback) {
			// summary: when loading view, callback should check if view is still being displayed before continuing, as well
			// as catch any syntax errors encountered while rendering view
			var origCallback = callback;
			var origErrback = errback;

			if (!valueOrPromise || !valueOrPromise.then) {
				var valueOrig = valueOrPromise;
				valueOrPromise = new Deferred();
				valueOrPromise.resolve(valueOrig);
			}

			if (callback) {
				callback = lang.hitch(this, function() {
					if (this.isValidAction(true)) {
						return origCallback.apply(null, arguments);
					}
				});
			}

			if (errback) {
				errback = lang.hitch(this, function() {
					if (this.isValidAction(true)) {
						return origErrback.apply(null, arguments);
					}
				});
			} else {
				errback = lang.hitch(this, this._onPublishError);
			}

			// add additional "then" to catch a javascript syntax error in the callback
			return valueOrPromise.then(callback, errback).then(null, lang.hitch(this, this._onPublishError));
		},

		_disconnect: function() {
			// summary: clear event connections
			if (this._handles) {
				array.forEach(this._handles, connect.disconnect);
				this._handles = null;
			}
		},

		_connectEvent: function(/*Object*/obj, /*String*/event, /*String|Function*/f) {
			// summary: connect to event
			this._registerEventHandle(connect.connect(obj, event, this, f));
		},

		_registerEventHandle: function(/*Object*/handle) {
			// summary: register the handle from the creation of the event handler
			this._handles = this._handles || [];
			this._handles.push(handle);
			return handle;
		},

		_getFixedFilters: function() {
			// summary: return query filters for popup view
			return this.m_action && this.m_action.m_fixedQuery ? this.m_action.m_fixedQuery.getElements() : null;
		},

		/**
		 * Return refNode which new node(s) will be inserted before
		 * @return {DOMNode}
		 */
		_getRefNodeNext: function() {
			return this.refNode;
		},

		/**
		 * Return refNode which new node(s) will be inserted after
		 * @return {DOMNode}
		 */
		_getRefNodePrev: function() {
			return this.widget.getRefNodeNext(this.refNode, true, true);
		},

		_getViewFilters: function() {
			// summary: return initial filters for popup view
			return this.m_action ? this.m_action.m_filtersView : null;
		},

		_resetModeless: function() {
			// summary: reset modeless state and subscription so that action is not interrupted by other actions
			if (!this.bModal) {
				this.bModal = true;
				this._handleUnfocus.remove();
				delete this._handleUnfocus;

				state.set("modelessAction", false);
			}
		},

		_resetProgressHandle: function() {
			// summary: reset members tracking publish status when having to repeat publish operation
			if (this._progressHandle) {
				clearTimeout(this._progressHandle);
				this._progressHandle = null;
			}
		},

		_resetPublishStatus: function() {
			// summary: reset members tracking publish status when having to repeat publish operation
			this._resetProgressHandle();
			this.m_response = null;
			this.m_error = null;
			this._errorCode = null;
			this._errorException = null;
			this._nPublished = 0;
			this._publishDfd = null;
		},

		/**
		 * Set error for controller
		 * @param {string|Object} err
		 */
		_setError: function(err) {
			if (typeof err === "string") {
				this._errorCode = null;
				this._errorException = null;
				this.m_error = err;
			} else {
				const {csiErrorCode, csiErrorException} = err;
				this._errorCode = csiErrorCode;
				this._errorException = csiErrorException;
				this.m_error = locale.getErrorMessage(err);
			}
		},

		_setRefNode: function(/*DOMNode*/refNode) {
			// summary: set refNode on which the action applies
			this.refNode = refNode;
			this.refNodeNext = refNode ? this._getRefNodeNext() : null;
			this.refNodePrev = refNode ? this._getRefNodePrev() : null;
		},

		_startTracking: function() {
			// summary: start tracking action and notify widget action is starting
			this.nTracking = Tracking.start(false, this.getTrackingId(), null, () => this._end());
			// console.log("Start Tracking(" + this.nTracking + "): " + this.getTrackingId());

			if (this.widget) {
				this.widget.preAction(this);
			}
		},

		hasToConfirmAction: function() {
			return !!this.m_action.confirmAction;
		},

		async _preAction() {
			// summary: do any prep work for action and determine if action is still valid.
			// Returns a Boolean value or Promise resolving to a Boolean value
			var cache = this.m_cache;
			var dfds = [];
			var requests = [];

			// call Action/@PreAction function
			var preAction = csi.resolveFunction(this.m_action.m_preAction, this.widget);
			if (preAction) {
				var dfd = preAction(this);
				if (dfd) {
					dfds.push(dfd);
				} else {
					return false;
				}
			}

			// allow additional Deferreds
			this.onPreActionDeferreds(dfds);

			// add request for dependency paths if specified
			var depPaths = this.m_action.dependencyPaths || [];

			this.onDependencyPaths(depPaths);

			if (depPaths.length) {
				var node = this.getNode() || this.getParent();
				if (node) {
					var request = comm.getNodeRequest(node.$URL, null, true).addDependencyPaths(depPaths);
					if (request = this.m_cache.reduceNodesRequest(request)) {
						requests.push(request);
					}
				}
			}

			// allow additional requests from custom controllers
			this._preActionRequests(requests);

			_.each(requests, function(req) {
				dfds.push(req.go().then(function(res) {
					cache.cacheResponse(res);
				}));
			});

			if (dfds.length) {
				return all(dfds).then(function(results) {
					// if we had preAction which returned Promise,
					// use its result as a resolved value
					return preAction ? results[0] : true;
				});
			}

			return true;
		},

		_preActionRequests: function(/*Request[]*/requests) {
			// summary: add any requests necessary to fetch data before executing action
		},

		_onAction: function(/*CallMethod*/call) {
			// summary: call Action/@OnAction
			const { _fOnAction, useReactForm } = this;

			if (useReactForm) {
				console.assert(!_fOnAction, "Addition logic should go to the Customer Action bundle file");
			}

			return _fOnAction?.(this, call);
		},

		/**
		 * Call Action/@ValidateAction if so configured - use onValidate for OOTB validation; leave this for customer (Site.js)
		 * @param {ViewWidget} [view]
		 * @return {Promise<void>}
		 */
		_validateAction(view) {
			return this._fValidateAction?.(this, view);
		},

		/**
		 * Validate form/table inputs; throw error if invalid
		 * @param {ViewWidget} view
		 * @return {Promise<void>}
		 */
		_validateForm: function(view) {
			var errorParams = {};

			if (!view.validateForm(errorParams)) {
				// FIX? - currently not used, so try to avoid using different error mechanisms
				console.assert(!errorParams.buttons);
				console.assert(!errorParams.iconClass);
				console.assert(!errorParams.onResponse);

				var message = errorParams.message;
				if (message == null) {
					message = this.getError(errorParams.suffix);
				}

				throw new Error(message);
			}
		},

		_end: function() {
			// summary: process end of action
			// console.log("End Tracking(" + this.nTracking + "): " + this.getTrackingId());

			if (this.nTracking) {
				if (this.endStatus) {
					message.success(this.getSuccess());

					// toggle action if applicable now that it has been applied
					if (this.m_element) {
						this.m_action.toggleControl(this.m_element);
					}
				}

				// start timer for publishing if not already done
				submitHoldWriter(true);

				// notify widget to end of action when it actually ends - so if this is nested action,
				// need to postpone until end of root action
				if (this.widget) {
					if (Tracking.inProgress()) {
						Tracking.addJob(lang.hitch(this.widget, this.widget.postAction, this));
					} else {
						this.widget.postAction(this, this.hasPendingAction());
					}
				}

				// notify observers
				topic.publish("/csi/action/end", this);

				// cleanup
				this._disconnect();
				delete this.nTracking;
				delete this.m_writer;
				delete this.m_response;

				// call extension point to do any cleanup work
				this.onEnd();

				// cleanup view reference last as it may be needed for onEnd processing
				delete this.m_widget;

				// track time last action ended
				state.set("lastActionEndTime", new Date().getTime());
			}

			// clear selection from document if shift or ctrl key was used to invoke action
			if (this._shiftKey || this._ctrlKey) {
				html.clearSelection();
			}

			this._otelSpan.end();
		},

		_writeRequest: function(/*Request?*/request, /*Function?*/getParameterValue) {
			// summary: write callMethod for given request
			request = request || this._getActionRequest();
			console.assert(!!request);
			return writerService.writeRequest(this.m_writer, request, getParameterValue);
		},

		_writeRequestParameters: function(/*CallMethod*/call) {
			// summary: add standard parameters to CallMethod operation
			call.addParameter(xml.PROP_URL, xml.ATTR_REF, this.m_urlNode);

			if (this.m_urlParent) {
				call.addParameter(xml.PROP_URL_PARENT, xml.ATTR_REF, this.m_urlParent);
			}

			if (this.m_idAttrParent) {
				call.addParameter(xml.PROP_ATTR_PARENT, xml.ATTR_STRING, this.m_idAttrParent);
			}

			if (this.keyContainer != null) {
				call.addParameter(xml.PROP_KEY, _.isNumber(this.keyContainer) ? xml.ATTR_INTEGER : xml.ATTR_STRING, this.keyContainer);
			}

			if ((this.m_bToggle === true) || (this.m_bToggle === false)) {
				call.addParameter("Toggle", xml.ATTR_BOOLEAN, !this.m_bToggle);
			}
		}
	});
	return Controller;
});
